import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Users, Clock, UserPlus, CreditCard,
  Lock, ArrowRight, TrendingUp, Inbox,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import api from '@/lib/axios';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { getEstadoCitaInfo } from '@/types/cita';
import { MisTareasWidget } from '@/components/tareas/MisTareasWidget';

const ADMIN_ROLES: string[] = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA];

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function getLastSixMonths() {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return { mes: MESES[d.getMonth()], total: 0 };
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub }: { label: string; value: string | number; sub: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50 mb-2 md:mb-3">
        {label}
      </p>
      <p className="text-2xl md:text-[2rem] font-bold text-text-primary leading-none">{value}</p>
      <p className="text-xs text-text-secondary mt-1.5 md:mt-2 line-clamp-2">{sub}</p>
    </div>
  );
}

function FinKpiCard({ label, value, sub, unit }: { label: string; value: string; sub: string; unit?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50 mb-2 md:mb-3">
        {label}
      </p>
      <p className="text-xl md:text-[1.6rem] font-bold text-text-primary leading-none">
        {value}
        {unit && <span className="text-xs md:text-sm font-normal text-text-secondary ml-1">{unit}</span>}
      </p>
      <p className="text-xs text-text-secondary mt-1.5 md:mt-2">{sub}</p>
    </div>
  );
}

function CobranzaRow({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <span className={cn('w-2.5 h-2.5 rounded-sm flex-shrink-0', color)} />
        <span className="text-sm text-text-secondary">{label}</span>
      </div>
      <span className="text-sm font-medium text-text-primary">{formatCurrency(value)}</span>
    </div>
  );
}

function EmptyState({ icon, title, desc, action }: {
  icon: React.ReactNode; title: string; desc: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-14 gap-3">
      <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center text-primary-400">
        {icon}
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-text-primary">{title}</p>
        <p className="text-xs text-text-secondary mt-0.5 max-w-[220px]">{desc}</p>
      </div>
      {action}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();
  const { usuario } = useAuthStore();
  const isAdmin = !!(usuario?.rolId && ADMIN_ROLES.includes(usuario.rolId));
  const today = new Date().toISOString().split('T')[0];

  const { data: stats } = useQuery<Record<string, any>>({
    queryKey: ['stats'],
    queryFn: () => api.get('/api/stats').then(r => r.data),
  });

  const { data: citasHoy = [], isLoading: loadingCitas } = useQuery<any[]>({
    queryKey: ['citas-dashboard', today],
    queryFn: () =>
      api.get(`/api/citas?fechaInicio=${today}T00:00:00&fechaFin=${today}T23:59:59`).then(r => r.data),
  });

  const enCurso = citasHoy.filter(c => c.estado === 'en_curso');
  // "En sala de espera" = estado 'espera' (paciente ya llegó a la clínica), no
  // confundir con las citas que aún no arrancan (programada/confirmada/espera),
  // que es el universo de candidatas para "próxima cita".
  const enSalaDeEspera = citasHoy.filter(c => c.estado === 'espera');
  const pendientes = citasHoy.filter(c => ['programada', 'confirmada', 'espera'].includes(c.estado));
  const proximaCita = [...pendientes].sort(
    (a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime()
  )[0];

  const chartData: { mes: string; total: number }[] =
    stats?.ingresosPorMes ?? getLastSixMonths();

  return (
    <div className="space-y-6">

      {/* ── KPIs principales ── */}
      <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          label="Citas hoy"
          value={stats?.citasHoy ?? citasHoy.length}
          sub={`${enSalaDeEspera.length} en sala de espera`}
        />
        <KpiCard
          label="En consulta ahora"
          value={enCurso.length}
          sub={enCurso.length === 0 ? 'Sin consultas activas' : `${enCurso.length} consulta${enCurso.length > 1 ? 's' : ''} activa${enCurso.length > 1 ? 's' : ''}`}
        />
        <KpiCard
          label="Próxima cita"
          value={proximaCita ? formatDate(proximaCita.fechaInicio, 'time') : '—'}
          sub={proximaCita?.paciente?.nombre ?? 'Agenda libre'}
        />
        <KpiCard
          label="Pacientes nuevos"
          value={stats?.pacientesNuevos ?? stats?.totalPacientes ?? 1}
          sub="este mes"
        />
      </div>

      {/* ── Contenido principal ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 md:gap-4">

        {/* Consultas de hoy */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 flex flex-col min-h-[340px]">
          <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50">
                Hoy · {citasHoy.length} citas
              </p>
              <h2 className="text-base font-bold text-text-primary mt-0.5">Consultas de hoy</h2>
            </div>
            <button
              onClick={() => navigate('/agenda')}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
            >
              Recepción <ArrowRight size={12} />
            </button>
          </div>

          <div className="flex-1 border-t border-gray-50">
            {loadingCitas ? (
              <div className="flex items-center justify-center h-full py-12">
                <div className="w-6 h-6 border-2 border-primary-100 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : citasHoy.length === 0 ? (
              <EmptyState
                icon={<Calendar size={24} />}
                title="Sin citas para hoy"
                desc="No hay citas programadas para el día de hoy."
                action={
                  <button
                    onClick={() => navigate('/agenda')}
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    + Nueva cita
                  </button>
                }
              />
            ) : (
              <div className="divide-y divide-gray-50 overflow-y-auto max-h-[400px]">
                {citasHoy.map((cita: any) => (
                  <div
                    key={cita.id}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() => cita.consultaId && navigate(`/consultas/${cita.consultaId}`)}
                  >
                    <span className="flex-shrink-0 text-xs font-bold text-text-secondary w-10">
                      {formatDate(cita.fechaInicio, 'time')}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 text-xs font-bold flex-shrink-0">
                      {cita.paciente?.nombre?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {cita.paciente?.nombre ?? 'Paciente'}
                      </p>
                      <p className="text-xs text-text-secondary truncate">
                        {[cita.medico?.nombre, cita.motivo].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className={cn(
                      'text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0',
                      getEstadoCitaInfo(cita.estado).softCls,
                    )}>
                      {getEstadoCitaInfo(cita.estado).label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Mis tareas (solo roles con acceso al módulo) */}
          <MisTareasWidget />

          {/* Acciones rápidas */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50 mb-3">
              Acciones rápidas
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/pacientes')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <UserPlus size={15} />
                Nuevo paciente
              </button>
              <button
                onClick={() => navigate('/agenda')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-text-primary text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Calendar size={15} />
                Nueva cita
              </button>
              <button
                onClick={() => navigate('/pagos')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-text-primary text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                <CreditCard size={15} />
                Registrar cobro
              </button>
            </div>
          </div>

          {/* Ocupación consultorios */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50">Ocupación</p>
                <h3 className="text-sm font-bold text-text-primary mt-0.5">Consultorios</h3>
              </div>
              <button
                onClick={() => navigate('/configuracion?tab=clinica')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
              >
                Gestionar <ArrowRight size={12} />
              </button>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5 py-1">
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Sala principal</p>
                  <p className="text-xs text-text-secondary">
                    {enCurso.length > 0 ? `${enCurso.length} consulta activa` : 'Libre el resto del día'}
                  </p>
                </div>
                <span className="ml-auto text-xs text-text-secondary">{enCurso.length} hoy</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sección Finanzas (solo admin) ── */}
      {isAdmin && (
        <div className="space-y-4 pt-2">
          {/* Separador */}
          <div className="flex items-center gap-2">
            <Lock size={10} className="text-text-secondary/40" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/40">
              Finanzas · Solo administración
            </span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* KPIs financieros */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            <FinKpiCard
              label="Ingresos del mes"
              value={formatCurrency(stats?.ingresosMes ?? 0)}
              sub="↑ +0% vs mes anterior"
              unit="MXN"
            />
            <FinKpiCard
              label="Cobrado este mes"
              value={formatCurrency(stats?.cobradoMes ?? 0)}
              sub="↑ 0% del ingreso"
              unit="MXN"
            />
            <FinKpiCard
              label="Cuentas por cobrar"
              value={formatCurrency(stats?.cuentasPorCobrar ?? 0)}
              sub="→ al corriente"
            />
            <FinKpiCard
              label="Ticket promedio"
              value={formatCurrency(stats?.ticketPromedio ?? 0)}
              sub={`→ ${stats?.totalCobros ?? 0} cobros`}
            />
          </div>

          {/* Gráfica + Cobranza */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 md:gap-4">

            {/* Ingresos por mes */}
            <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50">
                    Últimos 6 meses
                  </p>
                  <h3 className="text-base font-bold text-text-primary mt-0.5">Ingresos por mes</h3>
                </div>
                <button
                  onClick={() => navigate('/pagos')}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 transition-colors"
                >
                  Reportes <ArrowRight size={12} />
                </button>
              </div>

              {chartData.every(d => d.total === 0) ? (
                <EmptyState
                  icon={<Inbox size={22} />}
                  title="Sin ingresos en el período"
                  desc="Aún no hay cobros registrados estos meses."
                />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#5b7480' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#5b7480' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v: number) => [formatCurrency(v), 'Ingresos']}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                    />
                    <Bar dataKey="total" fill="#0c77a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Cobranza del mes */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5 flex flex-col">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50">
                Cobranza del mes
              </p>
              <p className="text-xl font-bold text-text-primary mt-0.5">
                {formatCurrency(stats?.ingresosMes ?? 0)}{' '}
                <span className="text-sm font-normal text-text-secondary">facturado</span>
              </p>

              <div className="mt-5 divide-y divide-gray-50 flex-1">
                <CobranzaRow
                  label="Cobrado"
                  value={stats?.cobradoMes ?? 0}
                  color="bg-green-500"
                />
                <CobranzaRow
                  label="Por cobrar vigente"
                  value={stats?.porCobrarVigente ?? 0}
                  color="bg-amber-400"
                />
                <CobranzaRow
                  label="Vencido"
                  value={stats?.vencido ?? 0}
                  color="bg-red-500"
                />
              </div>

              <button
                onClick={() => navigate('/pagos')}
                className="mt-5 w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-text-secondary hover:bg-gray-50 transition-colors"
              >
                Ver cuentas por cobrar →
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
